import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Share2, Facebook, Twitter } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getPostBySlug } from '../data/blogPosts';
import blogPosts from '../data/blogPosts';

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  // Scroll to top when navigating to a new post
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // If post not found, redirect to blog page
  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get related posts (same category, excluding current)
  const relatedPosts = blogPosts
    .filter(p => p.category === post.category && p.id !== post.id)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-[#0a0f1c] text-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');

        .prose {
          color: #d1d5db;
          line-height: 1.8;
        }
        .prose h2 {
          color: #ffffff;
          font-size: 1.75rem;
          font-weight: 700;
          margin-top: 2.5rem;
          margin-bottom: 1rem;
        }
        .prose h3 {
          color: #ffffff;
          font-size: 1.25rem;
          font-weight: 600;
          margin-top: 2rem;
          margin-bottom: 0.75rem;
        }
        .prose p {
          margin-bottom: 1.25rem;
        }
        .prose strong {
          color: #ffffff;
          font-weight: 600;
        }
        .prose ul, .prose ol {
          margin-bottom: 1.25rem;
          padding-left: 1.5rem;
        }
        .prose li {
          margin-bottom: 0.5rem;
        }
        .prose a {
          color: #10b981;
          text-decoration: underline;
        }
        .prose a:hover {
          color: #059669;
        }
        .prose blockquote {
          border-left: 4px solid #10b981;
          padding-left: 1rem;
          margin: 1.5rem 0;
          font-style: italic;
          color: #9ca3af;
        }
        .prose hr {
          border-color: #374151;
          margin: 2rem 0;
        }
        .prose table {
          width: 100%;
          border-collapse: collapse;
          margin: 1.5rem 0;
        }
        .prose th, .prose td {
          border: 1px solid #374151;
          padding: 0.75rem 1rem;
          text-align: left;
        }
        .prose th {
          background-color: #1f2937;
          color: #ffffff;
          font-weight: 600;
        }
        .prose code {
          background-color: #1f2937;
          padding: 0.2rem 0.4rem;
          border-radius: 0.25rem;
          font-size: 0.875rem;
        }

        .card-hover {
          transition: all 0.3s ease;
        }
        .card-hover:hover {
          transform: translateY(-4px);
        }
      `}</style>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4 bg-[#0a0f1c]/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/blog" className="flex items-center gap-2 text-white hover:text-[#10b981] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Quay Lại Blog</span>
          </Link>
          <div className="flex items-center gap-4">
            <button className="p-2 text-gray-400 hover:text-white transition-colors" title="Chia sẻ">
              <Share2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Image */}
      <div className="pt-16">
        <div className="w-full h-[50vh] md:h-[60vh] relative">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1c] via-[#0a0f1c]/50 to-transparent"></div>
        </div>
      </div>

      {/* Article Content */}
      <article className="relative -mt-32 z-10 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Article Header */}
          <header className="mb-12">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-gray-400">
              <span className="text-[#10b981] font-medium">{post.category}</span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(post.date)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.readTime} phút đọc
              </span>
            </div>
          </header>

          {/* Article Body */}
          <div className="prose max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content.replace(/\s*\{#[\w-]+\}/g, '')}</ReactMarkdown>
          </div>

          {/* Share Section */}
          <div className="mt-12 pt-8 border-t border-gray-800">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <span className="text-gray-400">Chia sẻ bài viết:</span>
              <div className="flex items-center gap-3">
                <button className="flex items-center gap-2 px-4 py-2 bg-[#1877F2] hover:bg-[#1877F2]/80 text-white rounded-lg transition-colors">
                  <Facebook className="w-4 h-4" />
                  Facebook
                </button>
                <button className="flex items-center gap-2 px-4 py-2 bg-[#1DA1F2] hover:bg-[#1DA1F2]/80 text-white rounded-lg transition-colors">
                  <Twitter className="w-4 h-4" />
                  Twitter
                </button>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-12 p-8 bg-gradient-to-r from-[#10b981]/20 to-[#059669]/10 rounded-2xl border border-[#10b981]/30">
            <h3 className="text-2xl font-bold mb-4">Cần Tư Vấn Về Vay Mua Nhà?</h3>
            <p className="text-gray-400 mb-6">
              Đội ngũ chuyên gia của Nhamy.us sẵn sàng hỗ trợ bạn bằng tiếng Việt. Tư vấn miễn phí, không cam kết.
            </p>
            <Link
              to="/"
              className="inline-block px-8 py-3 bg-[#10b981] hover:bg-[#059669] text-white font-semibold rounded-full transition-colors"
            >
              Nhận Tư Vấn Ngay
            </Link>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 px-6 mt-12">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold mb-8">Bài Viết Liên Quan</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link key={relatedPost.id} to={`/blog/${relatedPost.slug}`} className="block">
                  <div className="bg-[#141b2d] rounded-2xl overflow-hidden card-hover border border-gray-800">
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={relatedPost.featuredImage}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
                        <Calendar className="w-4 h-4" />
                        {formatDate(relatedPost.date)}
                      </div>
                      <h3 className="text-lg font-semibold text-white line-clamp-2 hover:text-[#10b981] transition-colors">
                        {relatedPost.title}
                      </h3>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-gray-800/50 py-12 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-500 text-sm">© 2026 Nhamy.us. Bảo lưu mọi quyền.</p>
        </div>
      </footer>
    </div>
  );
};

export default BlogPostPage;
